/**
 * Cloud-mode user menu. A small popover hanging off the user's
 * initial — shows the signed-in email and exposes Sign out. Lives
 * in the Landing header and (eventually) the editor topbar.
 *
 * Free mode never renders this.
 */

import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../auth/AuthContext.js';

import { AccountSettings } from './AccountSettings.js';
import { CreateOrgDialog } from './CreateOrgDialog.js';
import { OrgSettings } from './OrgSettings.js';
import './UserMenu.css';

export function UserMenu(): JSX.Element {
  const auth = useAuth();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [orgSettingsOpen, setOrgSettingsOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);

  useEffect(() => {
    const onDocClick = (e: MouseEvent): void => {
      if (!detailsRef.current?.open) return;
      if (e.target instanceof Node && !detailsRef.current.contains(e.target)) {
        detailsRef.current.open = false;
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && detailsRef.current?.open) {
        detailsRef.current.open = false;
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  if (auth.status !== 'authenticated' || !auth.user) return <></>;

  const initial = (auth.user.name ?? auth.user.email).slice(0, 1).toUpperCase();
  const close = (): void => {
    if (detailsRef.current) detailsRef.current.open = false;
  };
  const handleSignOut = async (): Promise<void> => {
    close();
    await auth.signOut();
  };
  const handleOrgSettings = (): void => {
    close();
    setOrgSettingsOpen(true);
  };
  const handleCreateOrg = (): void => {
    close();
    setCreateOrgOpen(true);
  };
  const handleAccountSettings = (): void => {
    close();
    setAccountSettingsOpen(true);
  };

  return (
    <>
      <details ref={detailsRef} className="user-menu">
        <summary
          className="user-menu-trigger"
          aria-haspopup="menu"
          aria-label={`Account menu for ${auth.user.email}`}
          title={auth.user.email}
        >
          <span className="user-menu-avatar" aria-hidden="true">
            {initial}
          </span>
        </summary>
        <div className="user-menu-popover" role="menu">
          <div className="user-menu-header">
            <div className="user-menu-name">{auth.user.name ?? auth.user.email}</div>
            {auth.user.name && <div className="user-menu-email">{auth.user.email}</div>}
          </div>

          {auth.organizations.length > 0 && (
            <div className="user-menu-section">
              <div className="user-menu-section-label">
                {auth.organizations.length > 1 ? 'Switch organization' : 'Organization'}
              </div>
              {auth.organizations.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={org.slug === auth.activeOrgSlug}
                  className={
                    'user-menu-item user-menu-item-org' +
                    (org.slug === auth.activeOrgSlug ? ' is-active' : '')
                  }
                  onClick={() => {
                    close();
                    void auth.switchOrg(org.slug);
                  }}
                  disabled={org.slug === auth.activeOrgSlug}
                >
                  <span className="user-menu-org-name">{org.name}</span>
                  {org.slug === auth.activeOrgSlug && (
                    <span className="user-menu-check" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              ))}
              <button
                type="button"
                role="menuitem"
                className="user-menu-item user-menu-item-create"
                onClick={handleCreateOrg}
              >
                + Create organization
              </button>
            </div>
          )}

          <button
            type="button"
            role="menuitem"
            className="user-menu-item"
            onClick={handleAccountSettings}
          >
            Account settings
          </button>
          {auth.activeOrgSlug && (
            <button
              type="button"
              role="menuitem"
              className="user-menu-item"
              onClick={handleOrgSettings}
            >
              Organization settings
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="user-menu-item user-menu-item-danger"
            onClick={() => void handleSignOut()}
          >
            Sign out
          </button>
        </div>
      </details>
      {auth.activeOrgSlug && (
        <OrgSettings
          orgSlug={auth.activeOrgSlug}
          open={orgSettingsOpen}
          onClose={() => setOrgSettingsOpen(false)}
        />
      )}
      {createOrgOpen && <CreateOrgDialog onClose={() => setCreateOrgOpen(false)} />}
      <AccountSettings open={accountSettingsOpen} onClose={() => setAccountSettingsOpen(false)} />
    </>
  );
}
